import { Children, ReactNode } from 'react';

import ChevronLeftIcon from '@/app/assets/svg/chevron-left.svg?react';
import ChevronRightIcon from '@/app/assets/svg/chevron-right.svg?react';
import { usePagination } from '@/app/shared/hooks/usePagination';
import { cn } from '@/app/shared/lib/cn';
import { Button } from '@/app/shared/ui/Button';
import { Skeleton } from '@/app/shared/ui/Skeleton';

export interface PaginatedListProps {
  children: ReactNode;
  itemsPerPage?: number;
  initialPage?: number;
  className?: string;
  paginationClassName?: string;
  forceShowPagination?: boolean;
  isLoading?: boolean;
  afterListSlot?: ReactNode;
}

const PaginatedList = ({
  children,
  itemsPerPage = 10,
  initialPage = 1,
  className = '',
  paginationClassName,
  forceShowPagination = false,
  isLoading = false,
  afterListSlot,
}: PaginatedListProps) => {
  const items = Children.toArray(children);

  const { currentPage, totalPages, paginatedData, nextPage, prevPage, hasNextPage, hasPrevPage } = usePagination({
    data: items,
    itemsPerPage,
    initialPage,
  });

  if (items.length === 0) return null;

  return (
    <div className='flex min-h-0 min-w-0 flex-col'>
      <div className={cn('flex flex-col gap-3', className)}>{paginatedData}</div>

      {afterListSlot}

      {(totalPages > 1 || forceShowPagination) && (
        <div className={cn('mt-auto flex items-center justify-between pt-7.5', paginationClassName)}>
          <Skeleton.Line loading={isLoading} className='h-9.5 w-fit'>
            <Button
              onClick={prevPage}
              disabled={!hasPrevPage}
              variant='secondary'
              className='h-9 w-9 bg-gray-800 p-0 text-gray-200 transition-none disabled:bg-gray-800 disabled:text-gray-200 disabled:opacity-40'
            >
              <ChevronLeftIcon className='h-4.5 w-4.5 text-gray-200' />
            </Button>
          </Skeleton.Line>

          <Skeleton.Line loading={isLoading} className='w-fit'>
            <p className='text-gray-500'>
              <span className='text-gray-200'>Page {currentPage}</span>/{totalPages}
            </p>
          </Skeleton.Line>

          <Skeleton.Line loading={isLoading} className='h-9.5 w-fit'>
            <Button
              variant='secondary'
              onClick={nextPage}
              disabled={!hasNextPage}
              className='h-9 w-9 bg-gray-800 p-0 text-gray-200 transition-none disabled:bg-gray-800 disabled:text-gray-200 disabled:opacity-40'
            >
              <ChevronRightIcon className='h-4.5 w-4.5 text-gray-200' />
            </Button>
          </Skeleton.Line>
        </div>
      )}
    </div>
  );
};

export default PaginatedList;
